import React from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAppStore } from "../../../appStore";
import { useAnalyticsStore } from "./AnalyticsStore/analyticsStore";
import { UserFilters } from "./AnalyticsUtils/analyticsInterfaces";
import { enUS, enGB } from "date-fns/locale";
import { updateSearchParamValues } from "../../utils/updateSearchParamValues";

export const StartDateDropDown = ({
  searchParams,
  setSearchParams,
}: {
  searchParams: URLSearchParams;
  setSearchParams: (searchParams: URLSearchParams) => void;
}) => {
  const {
    userFilters,
    setUserFilters,
    setAlertState,
    setShowMoreVisible,
    setShowMoreOpen,
    setDateChangesMade,
  } = useAnalyticsStore();

  const [startTimeAnchorEl, setStartTimeAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const startTimeOpen = Boolean(startTimeAnchorEl);
  const handleStartTimeClick = (event: React.MouseEvent<HTMLElement>) => {
    setStartTimeAnchorEl(event.currentTarget);
  };
  const handleStartTimeClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setStartTimeAnchorEl(null);
  };

  const { dateMMDDYYYY, setDateMMDDYYYY } = useAppStore();

  const selectedLocale = dateMMDDYYYY ? enUS : enGB;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={selectedLocale}
    >
      <DatePicker
        label={
          userFilters.start_date ? (
            <div className="flex">
              <>Start Date</>
              <div>
                <IconButton
                  aria-label="more"
                  id="long-button"
                  aria-controls={startTimeOpen ? "long-menu" : undefined}
                  aria-expanded={startTimeOpen ? "true" : undefined}
                  aria-haspopup="true"
                  onClick={handleStartTimeClick}
                  sx={{
                    marginLeft: "5px",
                    padding: 0,
                    color: "#000000",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="long-menu"
                  MenuListProps={{
                    "aria-labelledby": "long-button",
                  }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  anchorEl={startTimeAnchorEl}
                  open={startTimeOpen}
                  onClose={handleStartTimeClose}
                  slotProps={{
                    paper: {
                      style: {
                        maxHeight: 48 * 4.5,
                        width: "20ch",
                      },
                    },
                  }}
                >
                  <MenuItem
                    key="mm-dd-yyyy-start"
                    onClick={(event) => {
                      setDateMMDDYYYY(true);
                      handleStartTimeClose(event);
                    }}
                  >
                    MM/DD/YYYY
                  </MenuItem>
                  <MenuItem
                    key="dd-mm-yyyy-start"
                    onClick={(event) => {
                      setDateMMDDYYYY(false);
                      handleStartTimeClose(event);
                    }}
                  >
                    DD/MM/YYYY
                  </MenuItem>
                </Menu>
              </div>
            </div>
          ) : (
            <>Start Date</>
          )
        }
        value={userFilters.start_date}
        onChange={(newValue) => {
          if (newValue === null) {
            setUserFilters((previousState: UserFilters) => ({
              ...previousState,
              start_date: new Date(),
            }));
            updateSearchParamValues(
              {
                ...userFilters,
                start_date: new Date(),
              },
              searchParams,
              setSearchParams,
              ""
            );
            return;
          }
          setDateChangesMade(true);
          const furthestDate = new Date();
          furthestDate.setDate(furthestDate.getDate() - 62);
          setShowMoreVisible(false);
          setShowMoreOpen(false);
          if (localStorage.filtersSavedClicked === "true") {
            localStorage.setItem(
              "savedAnalyticsFilters",
              JSON.stringify({
                parent_group: userFilters.parent_group,
                site: userFilters.site,
                camera: userFilters.camera,
                start_date: newValue,
                end_date: userFilters.end_date,
              })
            );
          }
          setAlertState({
            dateTooFar: false,
            startAfterEnd: false,
            endBeforeStart: false,
          });

          if (
            newValue !== null &&
            newValue > furthestDate &&
            (newValue <= userFilters.end_date || userFilters.end_date === null)
          ) {
            setUserFilters((previousState: UserFilters) => ({
              ...previousState,
              start_date: newValue,
            }));
            updateSearchParamValues(
              {
                ...userFilters,
                start_date: newValue,
              },
              searchParams,
              setSearchParams,
              ""
            );
          }
          if (
            newValue !== null &&
            newValue <= furthestDate &&
            userFilters.end_date !== null
          ) {
            setAlertState({
              dateTooFar: true,
              startAfterEnd: false,
              endBeforeStart: false,
            });
          }
          if (
            newValue !== null &&
            newValue > userFilters.end_date &&
            userFilters.end_date !== null
          ) {
            setAlertState({
              dateTooFar: false,
              startAfterEnd: true,
              endBeforeStart: false,
            });
          }
        }}
        renderInput={(params) => (
          <TextField sx={{ width: 150 }} helperText="*Required" {...params} />
        )}
      />
    </LocalizationProvider>
  );
};
